import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  User, Phone, Mail, FileText, Camera, Upload, X, Save, Ban, Shield
} from 'lucide-react';

const categories = ['Regular', 'Wholesale', 'VIP', 'Staff', 'Other'];

const initialForm = {
  name: '',
  phone: '',
  email: '',
  cnic: '',
  category: '',
  notes: '',
  address: '',
};

export default function CustomerForm({ customer, onSubmit, onCancel, loading = false }) {
  const isEdit = !!customer;
  const [form, setForm] = useState(
    customer
      ? { ...initialForm, ...customer }
      : initialForm
  );
  const [errors, setErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(customer?.photo || null);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.phone.trim()) errs.phone = 'Phone number is required';
    else if (!/^[\d\s\+\-\(\)]{7,15}$/.test(form.phone.trim())) errs.phone = 'Invalid phone number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email address';
    if (form.cnic && !/^\d{13,15}$/.test(form.cnic.replace(/[-\s]/g, ''))) errs.cnic = 'Invalid CNIC (13-15 digits)';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm((prev) => ({ ...prev, photo: file }));
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit?.({
      fullName: form.name,
      phoneNumber: form.phone,
      cnicNumber: form.cnic,
      email: form.email,
      address: form.address,
      notes: form.notes,
      category: form.category,
    });
  };

  const inputClass = (field) => `
    w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border rounded-lg
    text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400
    transition-colors
    ${errors[field] ? 'border-red-500 dark:border-red-400' : 'border-gray-200 dark:border-gray-600'}
  `;

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-5"
    >
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
        <User size={20} />
        {isEdit ? 'Edit Customer' : 'Add Customer'}
      </h2>

      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 dark:border-gray-600">
          {photoPreview ? (
            <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <Camera size={22} className="text-gray-400" />
          )}
        </div>
        <label className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
          <Upload size={15} />
          {isEdit ? 'Change Photo' : 'Upload Photo'}
          <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
        </label>
        {photoPreview && (
          <button
            type="button"
            onClick={() => { setPhotoPreview(null); setForm((prev) => ({ ...prev, photo: null })); }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <X size={15} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Full Name *</label>
          <div className="relative">
            <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Full name" className={inputClass('name')} />
          </div>
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Phone Number *</label>
          <div className="relative">
            <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="tel" value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="03XX-XXXXXXX" className={inputClass('phone')} />
          </div>
          {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
          <div className="relative">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="email@example.com" className={inputClass('email')} />
          </div>
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">CNIC (Optional)</label>
          <div className="relative">
            <Shield size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={form.cnic} onChange={(e) => handleChange('cnic', e.target.value)} placeholder="XXXXX-XXXXXXX-X" className={inputClass('cnic')} />
          </div>
          {errors.cnic && <p className="mt-1 text-xs text-red-500">{errors.cnic}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Category</label>
          <select value={form.category} onChange={(e) => handleChange('category', e.target.value)} className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500">
            <option value="">Select category</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Address</label>
          <div className="relative">
            <div className="absolute left-3 top-3 text-gray-400"><FileText size={15} /></div>
            <textarea value={form.address} onChange={(e) => handleChange('address', e.target.value)} rows={2} placeholder="Address" className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400 resize-none" />
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Notes</label>
          <textarea value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} rows={3} placeholder="Additional notes..." className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400 resize-none" />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={loading} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50">
            <Ban size={16} />
            Cancel
          </button>
        )}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save size={16} />
          )}
          {isEdit ? 'Update Customer' : 'Save Customer'}
        </motion.button>
      </div>
    </motion.form>
  );
}
